// noinspection JSDeprecatedSymbols
import {useCommerceAPI} from '../commerce-api/contexts'
import {useMutation, useQuery} from '@tanstack/react-query'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

/**
 *
 * @param {'shopperOrders'|'shopperCustomersOcapi'} apiName
 * @param {String} methodName
 * @param {import("@tanstack/react-query").UseMutationOptions} [mutationOptions]
 * @return {import("@tanstack/react-query").UseMutationResult}
 * @see <a href="https://tanstack.com/query/latest/docs/react/reference/useMutation">TanStack Query `useMutation` reference</a> for more information about the return value.
 */
const useOcapiMutation = (apiName, methodName, mutationOptions) => {
    // noinspection JSDeprecatedSymbols
    const api = useCommerceAPI()

    if (!(api[apiName] instanceof Object)) {
        throw new Error(`API ${apiName} not found in OCAPI CommerceAPI`)
    }
    if (!(api[apiName][methodName] instanceof Function)) {
        throw new Error(`Method ${methodName} not found in ${apiName} OCAPI CommerceAPI`)
    }

    return useMutation({
        mutationFn: (...args) => api[apiName][methodName](...args),
        ...mutationOptions
    })
}

/**
 *
 * @param {'shopperCustomersOcapi'|'shopperOrders'|'shopperFolders'|'shopperSite'} apiName
 * @param {string} methodName
 * @param {{parameters: Object}|null} [apiOptions]
 * @param {import("@tanstack/react-query").UseQueryOptions} [queryOptions]
 * @return {import("@tanstack/react-query").UseQueryResult}
 * @see <a href="https://tanstack.com/query/latest/docs/react/reference/useQuery">TanStack Query `useQuery` reference</a> for more information about the return value.
 * @private
 */
const useOcapi = (apiName, methodName, apiOptions, queryOptions) => {
    // noinspection JSDeprecatedSymbols
    const api = useCommerceAPI()

    if (!(api[apiName] instanceof Object)) {
        throw new Error(`API ${apiName} not found in OCAPI CommerceAPI`)
    }
    if (!(api[apiName][methodName] instanceof Function)) {
        throw new Error(`Method ${methodName} not found in ${apiName} OCAPI CommerceAPI`)
    }

    return useQuery({
        queryKey: ['ocapi', apiName, methodName, apiOptions?.parameters ?? {}],
        queryFn: ({queryKey: [, apiName, methodName, parameters]}) =>
            api[apiName][methodName]({parameters}),
        ...queryOptions
    })
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions} [mutationOptions]
 * @return {import("@tanstack/react-query").UseMutationResult}
 * @see <a href="https://tanstack.com/query/latest/docs/react/reference/useMutation">TanStack Query `useMutation` reference</a> for more information about the return value.
 */
export const useOcapiPasswordResetMutation = (mutationOptions) =>
    useOcapiMutation('shopperCustomersOcapi', 'passwordReset', mutationOptions)

/**
 * @param {{headers?: Map<string,string>, parameters?: {locale?: string}}} apiOptions
 * @param {import("@tanstack/react-query").UseQueryOptions} [queryOptions]
 * @return {import("@tanstack/react-query").UseQueryResult}
 * @see <a href="https://tanstack.com/query/latest/docs/react/reference/useQuery">TanStack Query `useQuery` reference</a> for more information about the return value
 */
export const useOcapiDefaultSiteRaw = (apiOptions, queryOptions) =>
    useOcapi('shopperSite', 'getDefaultSite', apiOptions, {
        ...getConfig().app.queryCache?.static,
        ...queryOptions
    })

/**
 * @param {import("@tanstack/react-query").UseQueryOptions} [apiOptions]
 * @return {import("@tanstack/react-query").UseQueryResult}
 * @see <a href="https://tanstack.com/query/latest/docs/react/reference/useQuery">TanStack Query `useQuery` reference</a> for more information about the return value
 */
export const useOcapiFolder = (apiOptions) =>
    useOcapi(
        'shopperFolders',
        'getFolder',
        {...apiOptions},
        {
            ...getConfig().app.queryCache?.static
        }
    )

/**
 * @param {import("@tanstack/react-query").UseMutationOptions} [mutationOptions]
 * @return {import("@tanstack/react-query").UseMutationResult}
 * @see <a href="https://tanstack.com/query/latest/docs/react/reference/useMutation">TanStack Query `useMutation` reference</a> for more information about the return value.
 */
export const useOcapiFailOrderMutation = (mutationOptions) =>
    useOcapiMutation('shopperOrders', 'failOrder', mutationOptions)

/**
 * @param {import("@tanstack/react-query").UseMutationOptions} [mutationOptions]
 * @return {import("@tanstack/react-query").UseMutationResult}
 * @see <a href="https://tanstack.com/query/latest/docs/react/reference/useMutation">TanStack Query `useMutation` reference</a> for more information about the return value.
 */
export const useOcapiCancelOrderMutation = (mutationOptions) =>
    useOcapiMutation('shopperOrders', 'cancelOrder', mutationOptions)

/**
 * @param {import("@tanstack/react-query").UseMutationOptions} [mutationOptions]
 * @return {import("@tanstack/react-query").UseMutationResult}
 * @see <a href="https://tanstack.com/query/latest/docs/react/reference/useMutation">TanStack Query `useMutation` reference</a> for more information about the return value.
 */
export const useOcapiUpdateAdyenReferenceMutation = (mutationOptions) =>
    useOcapiMutation('shopperOrders', 'updateAdyenReference', mutationOptions)

/**
 * @param {import("@tanstack/react-query").UseMutationOptions} [mutationOptions]
 * @return {import("@tanstack/react-query").UseMutationResult}
 * @see <a href="https://tanstack.com/query/latest/docs/react/reference/useMutation">TanStack Query `useMutation` reference</a> for more information about the return value.
 */
export const useOcapiUpdateAdyenDataMutation = (mutationOptions) =>
    useOcapiMutation('shopperOrders', 'updateAdyenData', mutationOptions)

/**
 * @param {import("@tanstack/react-query").UseMutationOptions} [mutationOptions]
 * @return {import("@tanstack/react-query").UseMutationResult}
 * @see <a href="https://tanstack.com/query/latest/docs/react/reference/useMutation">TanStack Query `useMutation` reference</a> for more information about the return value.
 */
export const useOcapiUpdateCustomerDataMutation = (mutationOptions) =>
    useOcapiMutation('shopperOrders', 'updateCustomerData', mutationOptions)

export const useOcapiCustomerPasswordResetMutations = (mutationOptions) =>
    useOcapiMutation('shopperCustomersOcapi', 'passwordReset', mutationOptions)
